import {
  ApiACLRole,
  ApiAuthPermission,
  ApiAuthUser,
  ApiStatus,
} from "@incendium/api";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  GridSize,
  Stack,
  Typography,
} from "@mui/material";
import { ColsCol, ColsLayout } from "Components/ColsLayout/ColsLayout";
import GlassCard from "Components/GlassCard/GlassCard";
import SpacedListContainer from "Components/UI/SpacedListContainer";
import { useProjectUsers } from "features/users/hooks/userProjectUsers";
import { hasPermission, isTenantManagedUser } from "features/users/utils";
import { useNotification, useSelectedProject } from "Hooks";
import { useSave } from "Hooks/useSave";
import { getSelectedEnvironment } from "config";
import produce from "immer";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { useHistory, useParams } from "react-router-dom";
import { saveUser } from "features/users/services/userService";
import SavingButton from "Components/UI/SavingButton";
import { useUsers } from "Hooks/useUsers";
import UserRoles from "Components/Users/UserRoles";
import { useUser } from "Hooks/useUser";

const defaultColWidth = [6, 6];

interface IUserPageProps {
  isProject?: boolean;
}
function UserPage({ isProject }: IUserPageProps) {
  const { hasPermission: loggedinUserHasPermission } = useUser();
  const history = useHistory();
  const { save, saving } = useSave();
  const { userId } = useParams<{ userId: string }>();
  const userData = useUsers();
  const userProjectData = useProjectUsers();
  const { selectedClient, selectedProject } = useSelectedProject();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState<ApiStatus>(ApiStatus.STATUS_UNSPECIFIED);
  const [permissions, setPermissions] = useState<ApiAuthPermission[]>([]);
  const [colWidths, setColWidths] = useState<GridSize[]>(defaultColWidth);
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const formRef = useRef<HTMLFormElement | any>(null);

  const user = (isProject ? userProjectData : userData).users.find(
    (u) => u.id === userId,
  );

  const readOnly =
    userId !== "new" && Boolean(user && isTenantManagedUser(user));

  const selectedEnv = getSelectedEnvironment();
  const permissionsForCurrentEnv = useMemo(
    () =>
      (permissions || []).filter(
        (p) => (p.environment ?? null) === (selectedEnv ?? null),
      ),
    [permissions, selectedEnv],
  );

  useEffect(() => {
    if (!userId || userId === "new") {
      return;
    }

    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
      setPermissions(user.permissions || []);
      setStatus(user.status || ApiStatus.STATUS_UNSPECIFIED);
    }
  }, [userId, user]);

  const hasPermisions = useCallback(
    (role: ApiACLRole) => {
      return hasPermission(
        { permissions: permissionsForCurrentEnv },
        role,
        selectedClient!,
        selectedProject!,
      );
    },
    [permissionsForCurrentEnv, selectedClient, selectedProject],
  );

  const onChangePermsissions = useCallback(
    (role: ApiACLRole) => {
      setPermissions((p) =>
        produce(p, (draft) => {
          let idx = -1;
          if (isProject) {
            idx = draft.findIndex(
              (d) =>
                d.role === role &&
                d.projectId === selectedProject?.id &&
                (d.environment ?? null) === (selectedEnv ?? null),
            );
          } else {
            idx = draft.findIndex(
              (d) =>
                d.role === role &&
                d.clientId === selectedClient?.id &&
                d.projectId === selectedProject?.id &&
                (d.environment ?? null) === (selectedEnv ?? null),
            );
          }
          if (idx >= 0) {
            draft.splice(idx, 1);
          } else {
            draft.push({
              clientId: isProject ? undefined : selectedClient?.id,
              projectId: selectedProject?.id,
              role,
              environment: selectedEnv || undefined,
            });
          }
        }),
      );
    },
    [selectedClient, selectedProject, selectedEnv, isProject],
  );

  const onSubmit = useCallback(() => {
    if (readOnly) {
      return;
    }
    save(async () => {
      const user: ApiAuthUser = {
        id: userId,
        email,
        name,
        permissions,
        status,
      };

      try {
        const res = await saveUser(user);
        if (isProject) {
          userProjectData.setUsers((users) =>
            produce(users, (draft) => {
              const idx = draft.findIndex((d) => d.id === res.id);
              if (idx >= 0) {
                draft[idx] = res;
              } else {
                draft.push(res);
              }
            }),
          );
        }
        if (loggedinUserHasPermission(ApiACLRole.SUPER_ADMIN)) {
          userData.setUsers((users) =>
            produce(users, (draft) => {
              const idx = draft.findIndex((d) => d.id === res.id);
              if (idx >= 0) {
                draft[idx] = res;
              } else {
                draft.push(res);
              }
            }),
          );
        }
        showSuccessNotification(`User Saved`);
        history.push("../users");
      } catch (error) {
        showErrorNotification(
          `Failed to save user, please try again, ${error}`,
        );
      }
    });
  }, [
    userId,
    email,
    name,
    permissions,
    save,
    showSuccessNotification,
    showErrorNotification,
    history,
    status,
    isProject,
    userData,
    userProjectData,
    loggedinUserHasPermission,
    readOnly,
  ]);

  return (
    <Box sx={isProject ? { maxWidth: 1000 } : undefined}>
      <SpacedListContainer title="User Management">
        {readOnly && (
          <Typography color="warning.main" variant="body2" sx={{ mb: 2 }}>
            This user is managed by your organization&apos;s SSO. Their profile
            and permissions cannot be changed here.
          </Typography>
        )}
        <Grid container spacing={8} py={2}>
          <Grid item xs={isProject ? 6 : 5}>
            <ValidatorForm onSubmit={onSubmit} ref={formRef}>
              <ColsLayout spacing={3}>
                <ColsCol width={colWidths[0]} noborder>
                  <TextValidator
                    name="email"
                    variant="outlined"
                    fullWidth
                    label="Email"
                    size="small"
                    readonly={userId !== "new"}
                    disabled={userId !== "new" || readOnly}
                    value={email}
                    validators={["required"]}
                    errorMessages={["email is required"]}
                    onFocus={() => setColWidths([8, 4])}
                    onBlur={() => setColWidths(defaultColWidth)}
                    onChange={(e: React.FormEvent<HTMLInputElement>) => {
                      setEmail(e.currentTarget.value);
                    }}
                  />
                </ColsCol>
                <ColsCol width={colWidths[1]} noborder>
                  <TextValidator
                    name="name"
                    variant="outlined"
                    fullWidth
                    label="Name"
                    size="small"
                    value={name}
                    disabled={readOnly}
                    validators={["required"]}
                    errorMessages={["name is required"]}
                    onFocus={() => setColWidths([4, 8])}
                    onBlur={() => setColWidths(defaultColWidth)}
                    onChange={(e: React.FormEvent<HTMLInputElement>) => {
                      setName(e.currentTarget.value);
                    }}
                  />
                </ColsCol>
              </ColsLayout>
              <Stack my={4} spacing={1}>
                <Typography variant="subtitle1">Access Levels</Typography>
                {!isProject && (
                  <>
                    <Box>
                      <Typography variant="subtitle2">Super Admin</Typography>
                      <Typography variant="body1">
                        Super Admins can do everything
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2">Domain Admin</Typography>
                      <Typography variant="body1">
                        Can Manage every Client and every Project within this
                        environment.
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2">Client Admin</Typography>
                      <Typography variant="body1">
                        Can Manage a Client and every Project within that
                        client.
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2">
                        Client Observer
                      </Typography>
                      <Typography variant="body1">
                        Has read only access to every Project within the Client.
                      </Typography>
                    </Box>
                  </>
                )}
                <Box>
                  <Typography variant="subtitle2">Project Admin</Typography>

                  <Typography variant="body1">
                    Can change everything within the Project
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Project Observer</Typography>
                  <Typography variant="body1">
                    Has read only access to the Project
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Analytics Admin</Typography>
                  <Typography variant="body1">
                    Has Access to only the Analytics area of the Project, Can
                    create new reports
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">
                    Analytics Observer
                  </Typography>
                  <Typography variant="body1">
                    Has read only Access to only the Analytics area of the
                    Project, Cannot create any reports
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Button
                  color="secondary"
                  onClick={() => history.push("../users")}
                >
                  {readOnly ? "Back" : "Cancel"}
                </Button>
                {!readOnly && (
                  <>
                    <SavingButton
                      type="submit"
                      color="primary"
                      saving={saving}
                      disabled={permissions.length === 0}
                    >
                      {userId === "new" ? "Create" : "Save"}
                    </SavingButton>
                    {(userId === "new" ||
                      (status !== ApiStatus.ACTIVE && !user?.verifiedAt)) && (
                      <SavingButton
                        disabled={permissions.length === 0}
                        onClick={() => {
                          setStatus(ApiStatus.ACTIVE);
                          setTimeout(() => {
                            formRef.current.submit();
                          }, 1);
                        }}
                        color="primary"
                        saving={saving}
                      >
                        {userId === "new" ? "Create" : "Save"} & Invite
                      </SavingButton>
                    )}
                  </>
                )}
              </Stack>
            </ValidatorForm>
          </Grid>
          <Grid item xs={isProject ? 6 : 7}>
            <GlassCard>
              <Typography variant="h4">Change Permissions</Typography>
              {isProject ? (
                <>
                  <Stack direction="row" alignItems={"center"} mt={1}>
                    <FormControlLabel
                      sx={{ minWidth: 160 }}
                      disabled={readOnly}
                      control={
                        <Checkbox
                          size="small"
                          sx={{ pointerEvents: "none" }}
                          onChange={(e) => {
                            onChangePermsissions(ApiACLRole.PROJECT_ADMIN);
                          }}
                          checked={hasPermisions(ApiACLRole.PROJECT_ADMIN)}
                        />
                      }
                      label={"Project Admin"}
                    />
                    <FormControlLabel
                      sx={{ minWidth: 160 }}
                      disabled={
                        readOnly || hasPermisions(ApiACLRole.PROJECT_ADMIN)
                      }
                      control={
                        <Checkbox
                          size="small"
                          sx={{ pointerEvents: "none" }}
                          onChange={(e) => {
                            onChangePermsissions(ApiACLRole.PROJECT_OBSERVER);
                          }}
                          checked={hasPermisions(ApiACLRole.PROJECT_OBSERVER)}
                        />
                      }
                      label={"Project Observer"}
                    />
                  </Stack>
                  <Stack direction="row" alignItems={"center"}>
                    <FormControlLabel
                      sx={{ minWidth: 160 }}
                      disabled={
                        readOnly || hasPermisions(ApiACLRole.PROJECT_OBSERVER)
                      }
                      control={
                        <Checkbox
                          size="small"
                          sx={{ pointerEvents: "none" }}
                          onChange={(e) => {
                            onChangePermsissions(ApiACLRole.ANALYTICS_ADMIN);
                          }}
                          checked={hasPermisions(ApiACLRole.ANALYTICS_ADMIN)}
                        />
                      }
                      label={"Analytics Admin"}
                    />
                    <FormControlLabel
                      sx={{ minWidth: 160 }}
                      disabled={
                        readOnly || hasPermisions(ApiACLRole.ANALYTICS_ADMIN)
                      }
                      control={
                        <Checkbox
                          size="small"
                          sx={{ pointerEvents: "none" }}
                          onChange={(e) => {
                            onChangePermsissions(ApiACLRole.ANALYTICS_OBSERVER);
                          }}
                          checked={hasPermisions(ApiACLRole.ANALYTICS_OBSERVER)}
                        />
                      }
                      label={"Analytics Observer"}
                    />
                  </Stack>
                </>
              ) : (
                <UserRoles
                  permissions={permissions}
                  setPermissions={setPermissions}
                  selectedEnvironment={selectedEnv}
                  readOnly={readOnly}
                />
              )}
            </GlassCard>
          </Grid>
        </Grid>
      </SpacedListContainer>
    </Box>
  );
}

export default UserPage;
